import { AlertTriangle, Briefcase, CalendarPlus, Laptop, Thermometer, Zap } from 'lucide-react';
import type { EmploymentType, LeaveBalanceResponse } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDays, formatDaysHours, formatDateMedium } from '@/lib/hr';
import { t } from '@/i18n';

interface BalanceHeroProps {
  employmentType: EmploymentType | null;
  balance: LeaveBalanceResponse | null;
  loading: boolean;
  norm: number;
  year: number;
  sickDaysYtd: number;
  unavailabilityDaysYtd: number;
  remoteDaysYtd: number;
  onRequest: () => void;
}

/**
 * The employee's hero card. UOP: big remaining balance + a segmented
 * used/pending/remaining bar + the art. 168 overdue warning and secondary
 * counters. Non-UOP (B2B/zlecenie): an "unavailability, not leave" card —
 * the word "urlop" is deliberately absent (recharacterisation risk).
 */
export function BalanceHero({
  employmentType, balance, loading, norm, year,
  sickDaysYtd, unavailabilityDaysYtd, remoteDaysYtd, onRequest,
}: BalanceHeroProps) {
  const isUop = employmentType === 'employment_contract';

  if (loading) {
    return (
      <Card>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-10 w-36" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-4 w-64" />
        </CardContent>
      </Card>
    );
  }

  // UOP whose balance failed to load: keep the skeleton shape (a toast already
  // reported the failure) — never show the B2B copy to an employment contract.
  if (isUop && !balance) {
    return (
      <Card>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-10 w-36" />
          <Skeleton className="h-3 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!isUop || !balance) {
    return (
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-xl space-y-1.5">
            <div className="flex items-center gap-2">
              <Briefcase className="text-muted-foreground size-5" />
              <h2 className="text-lg font-semibold">
                {employmentType ? t(`employees.type.${employmentType}`) : t('hr.balance.b2bTitle')}
              </h2>
            </div>
            <p className="text-muted-foreground text-sm">
              {employmentType === null ? t('hr.balance.noContractType') : t('hr.balance.b2bBody')}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 pt-2 text-sm">
              <span className="text-muted-foreground">
                {t('hr.balance.unavailabilityYtd')}:{' '}
                <span className="text-foreground font-medium">{formatDays(unavailabilityDaysYtd)}</span>
              </span>
              <span className="text-muted-foreground">
                {t('hr.balance.remoteYtd')}:{' '}
                <span className="text-foreground font-medium">{formatDays(remoteDaysYtd)}</span>
              </span>
            </div>
          </div>
          <Button onClick={onRequest} className="shrink-0">
            <CalendarPlus className="size-4" />
            {t('hr.balance.reportUnavailability')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const usedHours = balance.usedDays * norm;
  const pendingHours = balance.pendingDays * norm;
  const totalHours = usedHours + pendingHours + Math.max(0, balance.remainingHours);
  const pct = (hours: number) => (totalHours > 0 ? `${(Math.max(0, hours) / totalHours) * 100}%` : '0%');
  const hasOverdue = balance.zalegly.days > 0;

  return (
    <Card>
      <CardContent className="flex flex-col gap-6 lg:flex-row">
        {/* Left: the number that matters + the segmented pool bar */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-muted-foreground text-sm">{t('hr.balance.remainingLabel', { year })}</p>
              <p className="mt-1 text-4xl font-bold tracking-tight tabular-nums">
                {formatDaysHours(balance.remainingHours, norm)}
              </p>
            </div>
            <Button onClick={onRequest}>
              <CalendarPlus className="size-4" />
              {t('hr.balance.request')}
            </Button>
          </div>

          <div
            className="bg-muted mt-5 flex h-3 w-full overflow-hidden rounded-full"
            role="img"
            aria-label={`${t('hr.balance.used')}: ${formatDays(balance.usedDays)}, ${t('hr.balance.pending')}: ${formatDays(balance.pendingDays)}, ${t('hr.balance.remaining')}: ${formatDays(balance.remainingDays)}`}
          >
            <div className="bg-indigo-500 dark:bg-indigo-400" style={{ width: pct(usedHours) }} />
            <div className="bg-amber-500 dark:bg-amber-400" style={{ width: pct(pendingHours) }} />
            <div className="bg-emerald-500 dark:bg-emerald-400" style={{ width: pct(balance.remainingHours) }} />
          </div>

          <div className="text-muted-foreground mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-indigo-500 dark:bg-indigo-400" />
              {t('hr.balance.used')}: <span className="text-foreground font-medium">{formatDays(balance.usedDays)}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-amber-500 dark:bg-amber-400" />
              {t('hr.balance.pending')}: <span className="text-foreground font-medium">{formatDays(balance.pendingDays)}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
              {t('hr.balance.remaining')}: <span className="text-foreground font-medium">{formatDays(balance.remainingDays)}</span>
            </span>
            <span>
              {t('hr.balance.entitlement')}:{' '}
              <span className="text-foreground font-medium">
                {balance.carriedOverDays > 0 || balance.adjustmentDays !== 0
                  ? t('hr.balance.entitlementValue', {
                      base: formatDays(balance.entitlementDays),
                      extra: formatDays(balance.carriedOverDays + balance.adjustmentDays),
                    })
                  : formatDays(balance.entitlementDays)}
              </span>
            </span>
          </div>
        </div>

        {/* Right: secondary counters */}
        <div className="grid shrink-0 grid-cols-2 gap-3 lg:w-72">
          <StatTile
            icon={<Zap className="size-4 text-cyan-600 dark:text-cyan-400" />}
            label={t('hr.balance.onDemand')}
            value={t('hr.balance.onDemandOf', { used: balance.onDemandUsedDays, cap: 4 })}
          />
          <StatTile
            icon={<Thermometer className="size-4 text-rose-600 dark:text-rose-400" />}
            label={t('hr.balance.sickDaysYtd')}
            value={formatDays(sickDaysYtd)}
          />
          <StatTile
            className={cn(
              'col-span-2',
              hasOverdue && 'border-amber-500/50 bg-amber-500/10',
            )}
            icon={
              hasOverdue ? (
                <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" />
              ) : (
                <Laptop className="text-muted-foreground size-4" />
              )
            }
            label={t('hr.balance.overdue')}
            value={
              hasOverdue
                ? t('hr.balance.overdueWarning', {
                    amount: formatDays(balance.zalegly.days),
                    deadline: formatDateMedium(balance.zalegly.deadline),
                  })
                : t('hr.balance.noOverdue')
            }
            valueClassName={hasOverdue ? 'text-amber-700 dark:text-amber-400 text-sm' : 'text-sm'}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function StatTile({
  icon, label, value, className, valueClassName,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  className?: string;
  valueClassName?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1 rounded-lg border p-3', className)}>
      <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
        {icon}
        {label}
      </span>
      <span className={cn('font-semibold tabular-nums', valueClassName)}>{value}</span>
    </div>
  );
}
